import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, CalendarCheck, ArrowLeft, ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { CALENDAR_URL_STANDARD } from "../mock";
import { BookingButton } from "../components/BookingButton";
import { TrustpilotBadge } from "../components/TrustpilotBadge";
import { track } from "../lib/analytics";

const SERVICES = {
  "full-stack-coaching": {
    title: "Full Stack Coaching",
    subtitle: "Every system, coached. The complete build.",
    facts: ["Transformation Guaranteed", "Private Client Mobile App", "Rx Club Membership Included", "1 on 1 Concierge Coaching"],
    features: [
      { name: "Fully Customized Muscle & Strength Program", detail: "Rebuilt when needed, delivered in-app with video demos" },
      { name: "Personalized Nutrition & Macro Plan", detail: "Adjusted at every check-in, not a meal plan you'll abandon" },
      { name: "Supplementation Protocol", detail: "There are some supplements that you will need to make life staples" },
      { name: "Cardiovascular Activity Dynamic Plan", detail: "Conditioning that builds your engine without stealing from your strength" },
      { name: "Gut Health Intervention", detail: "Digestion, bloating and absorption addressed: you can't use what you can't absorb" },
      { name: "Rest & Recovery Protocol", detail: "Sleep, deloads and stress load, where the results are actually made" },
      { name: "Weekly Check-Ins & Progress Adjustments", detail: "24 total, with written adjustments back within 24 hours" },
      { name: "Monthly Video Call & Form Checks", detail: "Six 1:1 calls, plus unlimited lift video review between them" },
      { name: "Priority Coach Access", detail: "Contact your coach Monday–Friday, priority response" },
      { name: "Travel, Plateau & Maintenance Protocols", detail: "Because it's a part of life" },
      { name: "Hormone Optimization", detail: "Comprehensive female optimization lab panel" },
      { name: "Metabolic Optimization", detail: "Provider-prescribed where clinically appropriate" },
      { name: "Licensed Provider Review & Interpretation", detail: "Results walked through with your coach, then acted on and monitored" },
      { name: "Rx Club Annual Membership", detail: "Gatekept, direct-to-patient pricing on lab tests, peptides, bioidentical hormones, medical weight loss drugs (when appropriate and provider prescribed), thyroid protocols and GI Mapping." },
    ],
    ctaHeadline: "Take It Back. No Apologies.",
    guarantee: {
      title: "The No More Wasted Time Guarantee",
      linkText: "How the Guarantee Works",
      intro:
        "You\u2019ve already given weeks or months to something that didn\u2019t work. Maybe more. So, here\u2019s ours in writing.",
      sections: [
        {
          title: "1. The first 28 days: The Right Fit Window",
          body: [
            "If this isn\u2019t right for you \u2014 the coaching, the pace, the fit with Sandra \u2014 tell us and we refund everything but your comprehensive plan build fee of $500. No forms, no exit interview, no talking you out of it.",
            "You keep the plan. The protocol we built for your systems is yours whether you stay or not. You paid for it, and you should walk out with something in your hands either way.",
          ],
        },
        {
          title: "2. Day 90: \u201cTwo Systems Better\u201d",
          body:
            "Your Matrix names the systems driving what you feel. We re-score all systems at day 90, against your own baseline from day one. If at least two of your systems haven\u2019t measurably improved, we keep coaching you for an additional three months (9 months total) at no extra cost to you. Same coach, same plan, same access \u2014 we just stop charging you until we get it right.",
        },
        {
          title: "What we need from you",
          body:
            "This only works if you show up. Log your food, complete 80% of your programmed sessions, and make your check-ins. All three are tracked in your app from day one, so you always know where you stand.",
        },
        {
          title: "And what you get from us",
          body:
            "You hear back from Sandra. Your plan gets reviewed and adjusted as needed regularly. If we miss that on our end, you get a month added free. The guarantee runs both directions, that\u2019s the whole point!",
        },
      ],
    },
    cta: { label: "Book Your Call to Learn More About Our Full Stack Coaching Program", href: CALENDAR_URL_STANDARD },
  },
  "lifestyle-coaching": {
    title: "Lifestyle & Longevity Coaching",
    subtitle: "The core systems, coached for the long game.",
    facts: ["Private Client Mobile App", "1 on 1 Coaching"],
    features: [
      { name: "Fully Customized Muscle & Strength Program", detail: "Rebuilt when needed, delivered in-app with video demos" },
      { name: "Personalized Nutrition & Macro Plan", detail: "Adjusted at every check-in, not a meal plan you'll abandon" },
      { name: "Supplementation Protocol", detail: "There are some supplements that you will need to make life staples" },
      { name: "Cardiovascular Activity Dynamic Plan", detail: "Conditioning that builds your engine without stealing from your strength" },
      { name: "Gut Health Intervention", detail: "Digestion, bloating and absorption addressed: you can't use what you can't absorb" },
      { name: "Rest & Recovery Protocol", detail: "Sleep, deloads and stress load, where the results are actually made" },
      { name: "Bi-Weekly Check-Ins & Progress Adjustments", detail: "Every other week, with written adjustments back within 24 hours" },
      { name: "Priority Coach Access", detail: "Contact your coach Monday–Friday, priority response" },
      { name: "Travel, Plateau & Maintenance Protocols", detail: "Because it's a part of life" },
      { name: "Discounted Rx Club Membership", detail: "Gatekept, direct-to-patient pricing on lab tests, peptides, bioidentical hormones, medical weight loss drugs (when appropriate and provider prescribed), thyroid protocols and GI Mapping." },
    ],
    ctaHeadline: "Take It Back. No Apologies.",
    cta: { label: "Book Your Call to Learn More About Our Lifestyle & Longevity Coaching Program", href: CALENDAR_URL_STANDARD },
  },
  "rx-club": {
    title: "Rx Club Membership",
    subtitle: "Direct-to-member clinical grade protocols.",
    facts: ["Coaching Member Benefit", "Our Hand-Picked Providers"],
    features: [
      { name: "Introduced Pricing", detail: "Pricing is set and billed directly by our independent medical provider partners, not by Sassy \u201cFull Stack\u201d Fitness. We do not sell, mark up, or take a fee from any medical product, prescription, or service." },
      { name: "Hormone & Metabolic", detail: "Introduction to medical weight loss (GLP) programs, metabolic support, and bioidentical hormone protocols offered by our provider partners and tailored to your needs." },
      { name: "Independent Licensed Oversight", detail: "All clinical and prescribing decisions are made independently by the licensed medical providers we introduce you to. Sassy \u201cFull Stack\u201d Fitness plays no role in, and receives no compensation from, any clinical decision." },
      { name: "Peptide Access", detail: "Peptides sourced by our provider partners from FDA-registered U.S. compounding pharmacies, under their independent clinical oversight." },
      { name: "Other Testing & Protocols", detail: "Labs, thyroid, and GI-MAP gut testing, plus other panels ordered and interpreted by our provider partners and acted on with your coach." },
    ],
    valueCallout: {
      value: "$699 Value",
      title: "Included with Select Coaching Programs",
    },
    cta: { label: "Book Your Strategy Session", href: CALENDAR_URL_STANDARD },
    disclaimer:
      "Sassy \u201cFull Stack\u201d Fitness is a coaching and wellness brand, not a medical provider, and does not sell, prescribe, or bill for any medication, hormone therapy, peptide, or lab service. Medical services shown above are provided independently by third-party licensed medical providers. Sassy \u201cFull Stack\u201d Fitness receives no commission, fee, or other compensation for any introduction, referral, prescription, or purchase made through our provider partners. Individual results may vary.",
  },
  "12-week-transformation": {
    title: "12-Week Transformation",
    subtitle: "Intermediate-to-advanced strength and metabolic reset.",
    facts: [],
    features: [
      { name: "Private Client Mobile App" },
      { name: "12-week progressive resistance training" },
      { name: "Whole-food nutrition & meal templates" },
      { name: "Weekly progress & consistency check-ins" },
      { name: "Education on metabolism & muscle preservation" },
      { name: "Focus on mobility & recovery" },
    ],
    ctaHeadline: "Take It Back. No Apologies.",
    cta: { label: "Purchase the 12-Week Transformation", href: "https://www.trainerize.me/checkout/sassyfitnessllc/Sandra.Guy?planGUID=0a74c050dfe444da98c2c3c0b36a4155&mode=checkout" },
    note: "Secure Checkout. Your intake form and program access will be sent after payment.",
  },
  "competition-coaching": {
    title: "Competition Coaching",
    subtitle: "So, You Want to Compete?",
    facts: [],
    paragraphs: [
      "Stepping onto the stage is a defining milestone, especially when navigating the unique metabolic and hormonal shifts of mid-life. If you are ready to trade your usual routine for the ultimate physique challenge, you are in exactly the right place.",
      "Sandra understands firsthand what it takes to rebuild a physique from the inside out during menopause, having pivoted her own athletic journey from a regional CrossFit champion to winning her first NPC Bikini contest at age 50.",
      "Backed by over 30 years of coaching experience and specialized expertise in hormone and peptide integration, she knows exactly how to safely and effectively peak a 40+ athlete for the stage without sacrificing long-term health.",
      "If you are ready to embrace the discipline and own your age under the stage lights, fill out the application below to start your prep journey.",
    ],
    beforeAfter: [
      {
        view: "Front",
        before: {
          label: "Before",
          src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-refresh-16/artifacts/oxl1pze9_Messenger_creation_79459DD8-7029-4EDC-B589-697706CA9474.jpeg",
        },
        after: {
          label: "On Stage",
          src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-refresh-16/artifacts/baqh03sv_Share-665038786906460156.webp",
        },
      },
      {
        view: "Back",
        before: {
          label: "Before",
          src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-refresh-16/artifacts/2vqogn2j_Messenger_creation_26A97AFF-902E-4FA4-9AB8-6F99583F89E2.jpeg",
        },
        after: {
          label: "On Stage",
          src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-refresh-16/artifacts/5524xtb0_4.webp",
        },
      },
    ],
    ctaHeadline: "Take It Back. No Apologies.",
    cta: { label: "Fill Out the Application", href: "/services/competition-coaching/apply", internal: true },
  },
};

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const svc = SERVICES[slug];
  const [guaranteeOpen, setGuaranteeOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (svc) document.title = `${svc.title} | Sassy Full Stack Fitness`;
  }, [slug, svc]);

  if (!svc) {
    return (
      <div className="bg-background min-h-screen flex flex-col">
        <Header minimal />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-white">
            That page doesn't exist.
          </h1>
          <button
            onClick={() => navigate("/")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-8 py-4 hover:brightness-110 transition focus-ring"
          >
            <ArrowLeft size={18} /> Back home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const isInternal = !!svc.cta.internal;
  const isBooking = !isInternal && svc.cta.href !== "#";
  const isCalendar = svc.cta.href === CALENDAR_URL_STANDARD;

  return (
    <div className="bg-background min-h-screen flex flex-col" data-testid="service-page">
      <Header minimal />
      <main className="flex-1 pt-24 md:pt-24 pb-20 px-[5%]">
        <div className="max-w-[1000px] mx-auto">
          {/* Hero */}
          <div className="text-center">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-white/50 hover:text-white/80 transition-colors mb-6 focus-ring rounded-sm"
              data-testid="service-back"
            >
              <ArrowLeft size={14} /> Sassy Full Stack Fitness
            </button>
            <h1
              className="font-display font-black uppercase leading-[1.05] text-[32px] md:text-[52px] text-white"
              style={{ letterSpacing: "0.01em" }}
              data-testid="service-title"
            >
              <span className="text-gold-gradient">{svc.title}</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/70 font-medium max-w-2xl mx-auto">
              {svc.subtitle}
            </p>
            {svc.facts.length > 0 && (
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                {svc.facts.map((f) => (
                  <span
                    key={f}
                    className="rounded-full border border-primary/40 bg-primary/[0.08] text-primary text-[11px] md:text-xs font-bold tracking-[0.12em] uppercase px-4 py-2"
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Before / After gallery (optional) — sits directly under the subtitle */}
          {svc.beforeAfter && (
            <div className="mt-6 md:mt-8" data-testid="service-before-after">
              <h2 className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl text-white text-center mb-3">
                See The <span className="text-gold-gradient">Transformation</span>
              </h2>
              <p className="text-center text-sm md:text-base text-white/55 mb-6 max-w-xl mx-auto">
                Rebuilt from the inside out through menopause: off-season to the stage.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
                {svc.beforeAfter.map((group) => (
                  <div
                    key={group.view}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-5"
                    data-testid={`before-after-${group.view.toLowerCase()}`}
                  >
                    <p className="text-center text-[11px] font-bold tracking-[0.22em] uppercase text-primary mb-3">
                      {group.view}
                    </p>
                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                      {[group.before, group.after].map((img, idx) => (
                        <figure
                          key={idx}
                          className="relative overflow-hidden rounded-xl bg-black border border-white/5"
                        >
                          <img
                            src={img.src}
                            alt={`Sandra — ${group.view.toLowerCase()} view, ${img.label.toLowerCase()}`}
                            loading="lazy"
                            className="w-full aspect-[9/16] object-cover object-top"
                          />
                          <figcaption className="absolute top-2.5 left-2.5 rounded-full bg-black/70 backdrop-blur-sm px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase text-white">
                            {img.label}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Prose (optional) or What's included */}
          {svc.paragraphs ? (
            <div className="mt-12 md:mt-16 max-w-3xl mx-auto space-y-6" data-testid="service-paragraphs">
              {svc.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-base md:text-lg text-white/75 leading-relaxed text-center md:text-left"
                >
                  {p}
                </p>
              ))}
            </div>
          ) : (
            <div className="mt-14 md:mt-20">
              <h2 className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl text-white text-center mb-10">
                What's <span className="text-gold-gradient">Included</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {svc.features.map((f, i) => {
                  const lastOdd =
                    svc.features.length % 2 === 1 && i === svc.features.length - 1;
                  return (
                    <div
                      key={f.name}
                      className={`flex gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6 ${
                        lastOdd ? "md:col-span-2 md:mx-auto md:w-[calc(50%-0.625rem)]" : ""
                      }`}
                    >
                      <span className="shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Check size={15} strokeWidth={3} />
                      </span>
                      <span>
                        <span className="block text-white font-bold leading-snug">{f.name}</span>
                        {f.detail && (
                          <span className="mt-1.5 block text-sm text-white/60 leading-relaxed">
                            {f.detail}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Value callout (optional) */}
          {svc.valueCallout && (
            <div className="mt-10 md:mt-12 rounded-2xl border border-primary/40 bg-primary/[0.07] p-7 md:p-8 text-center max-w-xl mx-auto" data-testid="service-value">
              <span className="inline-block font-display font-black uppercase tracking-tight text-gold-gradient text-3xl md:text-4xl">
                {svc.valueCallout.value}
              </span>
              <p className="mt-2 text-white font-bold uppercase tracking-[0.12em] text-sm">
                {svc.valueCallout.title}
              </p>
              {svc.valueCallout.body && (
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{svc.valueCallout.body}</p>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 md:mt-14 text-center">
            {svc.ctaHeadline && (
              <p className="font-display font-black uppercase tracking-tighter text-2xl md:text-4xl text-white mb-6" data-testid="service-cta-headline">
                {svc.ctaHeadline}
              </p>
            )}
            {svc.guarantee && (
              <div className="mb-8" data-testid="guarantee-block">
                <p className="font-display font-black uppercase tracking-tight text-lg md:text-2xl text-gold-gradient">
                  {svc.guarantee.title}
                </p>
                <button
                  type="button"
                  onClick={() => setGuaranteeOpen(true)}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm md:text-base font-bold text-primary underline underline-offset-4 decoration-2 decoration-primary/70 hover:decoration-primary hover:text-primary/90 transition-colors focus-ring rounded-sm"
                  data-testid="guarantee-link"
                >
                  {svc.guarantee.linkText}
                  <ArrowUpRight size={16} strokeWidth={2.5} />
                </button>
              </div>
            )}
            {isCalendar ? (
              <BookingButton
                href={svc.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                testid="service-cta"
                context={{ service: slug }}
                onClick={() => track("book_call_click", { location: "service", service: slug })}
              />
            ) : (
              <a
                href={svc.cta.href}
                onClick={
                  svc.cta.href === "#"
                    ? (e) => e.preventDefault()
                    : isInternal
                    ? (e) => {
                        e.preventDefault();
                        track("apply_click", { service: slug });
                        navigate(svc.cta.href);
                      }
                    : () =>
                        track("outbound_click", {
                          url: svc.cta.href,
                          label: svc.cta.label,
                          service: slug,
                        })
                }
                target={isBooking ? "_blank" : undefined}
                rel={isBooking ? "noopener noreferrer" : undefined}
                className="inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground font-black uppercase tracking-wide text-sm md:text-base px-9 py-4 hover:brightness-110 transition focus-ring shadow-[0_10px_40px_-8px_rgba(212,175,55,0.5)]"
                data-testid="service-cta"
              >
                <CalendarCheck size={18} />
                {svc.cta.label}
              </a>
            )}
            {svc.note && (
              <p className="mt-4 text-xs text-white/45 max-w-md mx-auto leading-relaxed">
                {svc.note}
              </p>
            )}
            <div className="mt-5 flex justify-center" data-testid="service-trust">
              <TrustpilotBadge />
            </div>
          </div>

          {/* Legal disclaimer (optional) */}
          {svc.disclaimer && (
            <p className="mt-12 text-[11px] text-white/35 leading-relaxed max-w-3xl mx-auto text-center" data-testid="service-disclaimer">
              {svc.disclaimer}
            </p>
          )}
        </div>
      </main>

      {/* Guarantee terms lightbox */}
      {svc.guarantee && (
        <Dialog open={guaranteeOpen} onOpenChange={setGuaranteeOpen}>
          <DialogContent
            className="max-w-lg bg-background border-white/15 text-white max-h-[85vh] overflow-y-auto"
            data-testid="guarantee-modal"
          >
            <DialogHeader>
              <DialogTitle className="font-display font-black uppercase tracking-tight text-2xl text-gold-gradient text-left">
                {svc.guarantee.title}
              </DialogTitle>
              <DialogDescription className="text-white/70 text-left leading-relaxed text-[15px]">
                {svc.guarantee.intro}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 mt-1">
              {svc.guarantee.sections.map((s, i) => (
                <div key={i}>
                  <h4 className="font-bold text-white text-[15px] mb-1.5">{s.title}</h4>
                  {Array.isArray(s.body) ? (
                    s.body.map((p, j) => (
                      <p key={j} className="text-sm text-white/65 leading-relaxed mb-2 last:mb-0">
                        {p}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm text-white/65 leading-relaxed">{s.body}</p>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
      <Footer />
    </div>
  );
};

export default ServicePage;
